import React from "react";
import { useRouter } from "next/router";
import { Hooks } from "@xt/xtbase";
import strategyIconFallback from "assets/images/aicopy/eb3a5f42-0255-4b85-82c6-bd5462a3928b.png";
import { getProfitColorClass } from "../../utils/colorScheme";
import styles from "./index.module.less";

const { useTranslation } = Hooks;
const AI_TYPE_TABS = [
  { value: "", label: "All" },
  { value: "ChatGPT", label: "ChatGPT" },
  { value: "Claude", label: "Claude" },
  { value: "Gemini", label: "Gemini" },
  { value: "Grok", label: "Grok" },
];

function pickStrategyName(strategyNames, locale) {
  if (!Array.isArray(strategyNames) || strategyNames.length === 0) return null;
  const raw = (locale || "en").toLowerCase().replace(/_/g, "-");
  const primary = raw.split("-")[0];
  const langMap = { cn: "zh", zh: "zh" };
  const preferred = langMap[primary] || primary;
  const order = [preferred, "en", "zh", "pt", "ar"];
  const codes = [...new Set(order)];
  for (const code of codes) {
    const hit = strategyNames.find((x) => (x.language || "").toLowerCase() === code);
    if (hit?.name) return hit.name;
  }
  return strategyNames[0]?.name || "—";
}

function formatTotalPnl(s) {
  const n = parseFloat(String(s ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  const [intPart, dec] = abs.toFixed(2).split(".");
  const intWithSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const numStr = `${intWithSep}.${dec}`;
  if (n > 0) return `+${numStr}`;
  if (n < 0) return `-${numStr}`;
  return numStr;
}

function rankNumClass(rank) {
  if (rank === 1) return styles.rankNum1;
  if (rank === 2) return styles.rankNum2;
  if (rank === 3) return styles.rankNum3;
  return styles.rankNumRest;
}

/**
 * AI Lab — PNL 排行（GET /public/ai-lab/stats/ranking）
 * @param {Array<{ strategyId: number|string, strategyNames?: Array, avatarUrl?: string|null, tags?: string[]|null, totalProfit?: string }>} items
 * @param {boolean} loading
 */
export default function AiLabPnlRankingCard({ items = [], loading = false, activeAiType = "", onAiTypeChange }) {
  const t = useTranslation();
  const router = useRouter();
  const locale = router?.locale || "en";

  const rows = Array.isArray(items) ? items : [];

  return (
    <div className={`${styles.card} ${loading ? styles.cardLoading : ""}`}>
      <div className={styles.titleRow}>
        <h3 className={styles.moduleTitle}>{t("aicopy.aiLabPnlTitle")}</h3>
      </div>

      <div className={styles.tabScroll}>
        {AI_TYPE_TABS.map((tab) => {
          const active = activeAiType === tab.value;
          const label = tab.value === "" ? t("aicopy.aiLabPnlTabAll") : tab.label;
          return (
            <button
              key={tab.value || "all"}
              type="button"
              className={active ? styles.tabActive : styles.tab}
              onClick={() => onAiTypeChange && onAiTypeChange(tab.value)}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className={styles.tableWrap}>
        <div className={styles.tableHead}>
          <span className={styles.thRank}>{t("aicopy.aiLabPnlColRank")}</span>
          <span className={styles.thStrategy}>{t("aicopy.aiLabPnlColStrategy")}</span>
          <span className={styles.thSignal}>{t("aicopy.aiLabPnlColTags")}</span>
          <span className={styles.thPnl}>{t("aicopy.aiLabPnlColTotalProfit")}</span>
        </div>

        <div className={styles.tableBody}>
          {loading && rows.length === 0 && (
            <div className={styles.emptyHint}>{t("aicopy.aiLabPnlLoading")}</div>
          )}
          {!loading && rows.length === 0 && (
            <div className={styles.emptyHint}>{t("aicopy.aiLabPnlEmpty")}</div>
          )}
          {rows.map((row, index) => {
            const rank = index + 1;
            const name = pickStrategyName(row.strategyNames, locale) || "—";
            const avatar = row.avatarUrl || strategyIconFallback;
            const aiType = row.aiType || activeAiType || "";
            const tagText =
              aiType || (Array.isArray(row.tags) && row.tags.length > 0 ? row.tags[0] : "—");
            const subTag =
              Array.isArray(row.tags) && row.tags.length > 1
                ? row.tags.slice(1).join(" · ")
                : "";
            const pnlStr = formatTotalPnl(row.totalProfit);
            const pnlNum = parseFloat(String(row.totalProfit ?? "").replace(/,/g, ""));
            const pnlClass = getProfitColorClass(pnlNum, {
              rise: styles.pnlRise,
              fall: styles.pnlFall,
            });
            const signalClassName = aiType ? `signal_${String(aiType).toLowerCase()}` : "signal_default";

            return (
              <div
                key={row.strategyId != null ? String(row.strategyId) : `rank-${rank}`}
                className={styles.tr}
              >
                <div className={styles.tdRank}>
                  <div className={styles.rankBadge}>
                    <span className={rankNumClass(rank)}>{rank}</span>
                  </div>
                </div>
                <div className={styles.tdStrategy}>
                  <div className={styles.strategyAvatarWrap}>
                    <img
                      className={styles.strategyAvatar}
                      src={avatar}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.src = strategyIconFallback;
                      }}
                    />
                  </div>
                  <div className={styles.strategyText}>
                    <span className={styles.strategyName}>{name}</span>
                    {subTag ? (
                      <span className={styles.strategyTag}>{subTag}</span>
                    ) : null}
                  </div>
                </div>
                <div className={styles.tdSignal}>
                  <span className={`${styles.signalPill} ${styles[signalClassName] || styles.signal_default}`}>
                    {tagText}
                  </span>
                </div>
                <div className={styles.tdPnl}>
                  <span className={`${styles.pnlValue} ${pnlClass || ""}`}>{pnlStr}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
